import { styled } from '@mui/material';
import { isEmptyArray, isEmptyDateRange } from './helpers';
import { formatUtcDate } from './formatDate';

export const FILTER_TYPES = {
    CHECKBOX: 'checkbox',
    FLAG: 'flag',
    DATE_RANGE: 'dateRange',
    NUMERIC_RANGE: 'numericRange',
};

export const getDefaultValues = (filters) => {
    const defaultValues = {};
    filters.forEach((filter) => {
        if (filter.defaultValue !== undefined) {
            defaultValues[filter.name] = filter.defaultValue;
        }
    });
    return defaultValues;
};

const isEmptyNumericRange = (v) =>
    !v || ((v.from === null || v.from === undefined) && (v.to === null || v.to === undefined));

export const isAppliedFilterValue = (value) => {
    if (Array.isArray(value)) return !isEmptyArray(value);
    if (value && typeof value === 'object') {
        if ('from' in value || 'to' in value) {
            // Works for both date ranges and numeric ranges
            return !isEmptyDateRange(value) || !isEmptyNumericRange(value);
        }
        // Plain object without from/to → not a range
        return false;
    }
    return value !== null && value !== undefined && value !== '';
};

export const countTotalNumFilters = (filters) => {
    let count = 0;

    Object.values(filters).forEach((value) => {
        if (isAppliedFilterValue(value)) {
            count++;
        }
    });

    return count;
};

const getDateRangeFilterApiMappings = (name, value) => {
    switch (name) {
        case 'transactionDate':
            return [
                { key: 'transactionDateStart', value: value.from },
                { key: 'transactionDateEnd', value: value.to },
            ];
        case 'startDate':
            return [
                { key: 'startDateStart', value: value.from },
                { key: 'startDateEnd', value: value.to },
            ];
        case 'endDate':
            return [
                { key: 'endDateStart', value: value.from },
                { key: 'endDateEnd', value: value.to },
            ];
        case 'postDate':
            return [
                { key: 'postDateStart', value: value.from },
                { key: 'postDateEnd', value: value.to },
            ];
        case 'periodEndDate':
            return [
                { key: 'periodEndDateStart', value: value.from },
                { key: 'periodEndDateEnd', value: value.to },
            ];
        case 'submittedDate':
            return [
                { key: 'submittedDateStart', value: value.from },
                { key: 'submittedDateEnd', value: value.to },
            ];
        case 'payrollDate':
            return [
                { key: 'payDateStart', value: value.from },
                { key: 'payDateEnd', value: value.to },
            ];
        case 'weekEndingDate':
            return [
                { key: 'weekEndStart', value: value.from },
                { key: 'weekEndEnd', value: value.to },
            ];
        case 'monthPosted':
            return [
                { key: 'monthPostedStart', value: value.from },
                { key: 'monthPostedEnd', value: value.to },
            ];
        case 'transactionDateRange':
            return [
                { key: 'transactionDateStart', value: value.from },
                { key: 'transactionDateEnd', value: value.to },
            ];
        case 'invoiceDate':
            return [
                { key: 'invoiceStartDate', value: value.from },
                { key: 'invoiceEndDate', value: value.to },
            ];
        case 'postingDate':
            return [
                { key: 'postStartDate', value: value.from },
                { key: 'postEndDate', value: value.to },
            ];
        default:
            return [
                { key: `${name}Start`, value: value.from },
                { key: `${name}End`, value: value.to },
            ];
    }
};

const getNumericRangeFilterApiMappings = (name, value) => {
    switch (name) {
        case 'bookingId':
            return [
                { key: 'bookingIdRangeStart', value: Number(value.from) },
                { key: 'bookingIdRangeEnd', value: Number(value.to) },
            ];
        default:
            return [
                { key: `${name}From`, value: Number(value.from) },
                { key: `${name}To`, value: Number(value.to) },
            ];
    }
};

// Maps filters to the format used by the API. At the moment only date-range filters needs to be formatted.
export const formatFiltersForApi = (filtersValues, filtersSetup) =>
    Object.entries(filtersValues ?? {}).reduce((acc, [key, value]) => {
        if (!isAppliedFilterValue(value)) return acc;

        const filterConfig = filtersSetup.find((f) => f.name === key);

        if (!filterConfig) return acc; // filter may no longer exist, do not include it in the API call

        if (filterConfig.type === FILTER_TYPES.DATE_RANGE) {
            const dateMappings = getDateRangeFilterApiMappings(key, value);
            if (dateMappings && dateMappings.length) {
                dateMappings.forEach((mapping) => {
                    acc[mapping.key] = mapping.value;
                });
            }
        } else if (filterConfig.type === FILTER_TYPES.NUMERIC_RANGE) {
            const numericRangeMappings = getNumericRangeFilterApiMappings(key, value);
            if (numericRangeMappings && numericRangeMappings.length) {
                numericRangeMappings.forEach((mapping) => {
                    acc[mapping.key] = mapping.value;
                });
            }
        } else if (filterConfig.type === FILTER_TYPES.FLAG) {
            value?.forEach((flag) => {
                acc[flag] = true;
            });
        } else if (Array.isArray(value)) {
            // As BE does not support arrays, we need to join the values to send them as a string :(
            // Example: "statuses[]=BpHold&statuses[]=TCHold" get converted to "statuses=BpHold&statuses=TCHold"
            acc[key] = value.join(`&${filterConfig.name}=`);
        } else {
            acc[key] = value;
        }

        return acc;
    }, {});

// Maps filters to the format used by the Invoice API.

// Returns filter value in a human readable format.
export const getFilterDescription = (value, type, options) => {
    if ((type === FILTER_TYPES.CHECKBOX || type === FILTER_TYPES.FLAG) && Array.isArray(value)) {
        const hasObjectOptions = options && options?.[0]?.value;
        // For fields that have custom options, we need to return the label of the selected value
        if (hasObjectOptions) {
            return value?.map((v) => options.find((o) => o.value === v)?.label).join(', ');
        }
        return value?.join(', ');
    }
    if (type === FILTER_TYPES.DATE_RANGE) {
        let dateRangeDescription = '';
        if (value.from) {
            dateRangeDescription = `From ${formatUtcDate(value.from)}`;
        }
        if (value.to) {
            dateRangeDescription = `${
                dateRangeDescription ? `${dateRangeDescription} - ` : ''
            }To ${formatUtcDate(value.to)}`;
        }
        return dateRangeDescription;
    }
    if (type === FILTER_TYPES.NUMERIC_RANGE) {
        let numericRangeDescription = '';
        if (value.from) {
            numericRangeDescription = `From ${value.from}`;
        }
        if (value.to) {
            numericRangeDescription = `${
                numericRangeDescription ? `${numericRangeDescription} - ` : ''
            }To ${value.to}`;
        }
        return numericRangeDescription;
    }
    return '';
};

// Returns the applied filters in a human readable format.
export const getAppliedFiltersDescription = (appliedFilters, filtersSetup) =>
    Object.entries(appliedFilters ?? {}).reduce((acc, [filterKey, filterValue]) => {
        const filterConfig = filtersSetup.find((f) => f.name === filterKey);
        const isApplied = isAppliedFilterValue(filterValue);
        if (filterConfig && isApplied) {
            acc.push({
                label: filterConfig.label,
                description: getFilterDescription(
                    filterValue,
                    filterConfig.type,
                    filterConfig?.overrides?.options
                ),
            });
        }
        return acc;
    }, []);

const FILTER__DESCRIPTION_SEPARATOR = '•';
const FilterDescription = styled('span')({
    '&:not(:first-of-type)::before': {
        content: `"${FILTER__DESCRIPTION_SEPARATOR}"`,
        margin: '0 10px',
    },
});
export const renderAppliedFiltersDescription = (filters, filtersSetup) => {
    const appliedFilters = getAppliedFiltersDescription(filters, filtersSetup);
    return (
        <>
            <strong style={{ margin: '0 12px 0 4px' }}>{FILTER__DESCRIPTION_SEPARATOR}</strong>
            {appliedFilters.length === 0 ? (
                <FilterDescription>No filters applied.</FilterDescription>
            ) : (
                <>
                    <strong>Filtered by: </strong>
                    {appliedFilters.map((filter) => (
                        <FilterDescription key={filter.label}>
                            <strong>{`${filter.label}: `}</strong>
                            {filter.description}
                        </FilterDescription>
                    ))}
                </>
            )}
        </>
    );
};
